import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "../../../design-system";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import DemoVideoPicker from "./DemoVideoPicker";
import { getDemoShareSrc, DEMO_SRC_EVENT } from "./demoSource";
import logoSite from "../../../../assets/brand/logo_site.svg";
import logoPurple from "../../../../assets/brand/logo_site_purple.svg";

/**
 * FlagshipEtTimeline — the "pick clips → watch them land on a timeline" surface.
 *
 * Faithful, isolated copy of the module-private `EtOverTimeline` orchestrator from
 * SpreadsheetTimelinePage.tsx, together with its ENTIRE dependency closure
 * (EditTimeline, CutPlayer, ChatDock, FrameTiles + all helpers/types and the two-way
 * cc-et-picks / cc-et-cmd postMessage bridge). Nothing here is refactored — it is a
 * verbatim extraction so the component can be embedded on its own.
 *
 * It embeds the live ET iframe (via getDemoShareSrc/DEMO_SRC_EVENT), reads the ordered
 * pick list the ET posts out on every selection change, and renders a Premiere/CapCut-style
 * timeline + a double-buffered preview player where the ticked clips assemble in pick order.
 *
 * Every claim mirrors ET_BEHAVIOR_SPEC.md. Nothing here is invented.
 */

const font = "Outfit, sans-serif";
const mono = "'IBM Plex Mono', 'SFMono-Regular', Consolas, monospace";
const CHERRY = "#e61d50";
const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
const iframeStyle = (scale: number): React.CSSProperties => ({
  position: "absolute", top: 0, left: 0, width: `${100 / scale}%`, height: `${100 / scale}%`,
  transform: `scale(${scale})`, transformOrigin: "top left", border: "none", background: "#0b0a0f",
});
const zoomBtn = { cursor: "pointer", background: "transparent", border: "none", color: "#c2c7d6", fontFamily: font, fontSize: "14px", lineHeight: 1, px: "8px", py: "3px", "&:hover": { color: "#fff", background: "rgba(255,255,255,0.08)" } };

/* ── the pick bridge payload (mirrors createVideo.jsx's cc-et-picks message) ── */
type ClipId = number | string; // split pieces get string ids ("123-a")
type Pick = {
  id: ClipId; order: number; start: number; end: number; sec: number; trimmed?: boolean;
  sentence: string; speaker: string; sentiment: string; thumb: string;
};
/** Commands the timeline can send back to the embedded ET (see createVideo.jsx bridge).
 *  Trim deltas are integer steps of 0.1s; a drag gesture batches into one command (one undo). */
type EtCmd =
  | { cmd: "adjust"; id: ClipId; deltaStart: number; deltaEnd: number }
  | { cmd: "reorder"; ids: ClipId[] }
  | { cmd: "select"; ids: ClipId[] }
  | { cmd: "deselect"; id: ClipId }
  | { cmd: "split"; id: ClipId; t: number }
  | { cmd: "blackcherry" }
  | { cmd: "ai-chat"; text: string };
type SendCmd = (c: EtCmd) => void;
type ChatMsg = { role: "user" | "assistant"; text: string; applied?: number };

/* ══════════════════ REAL FILMSTRIP FRAMES (client-side extraction) ══════════════════ */
/** The backend only thumbnails one poster per clip, so the timeline used to repeat that one
 *  frame. This extracts REAL frames in the browser: a hidden <video> seeks through the clip
 *  (~1 frame per second, ≤8 tiles) and grabs each frame onto a canvas. If the video host
 *  blocks canvas reads (CORS), we quietly fall back to the repeated poster. */
const frameCache = new Map<string, string>(); // "url|t" -> dataURL
const brokenFrameUrls = new Set<string>(); // hosts where canvas reads are CORS-tainted
type Extractor = { url: string; video: HTMLVideoElement; queue: number[]; busy: boolean; listeners: Set<() => void> };
let frameExtractor: Extractor | null = null;

const gridT = (t: number): number => Math.round(t * 2) / 2; // 0.5s grid → cache hits across trims

function pumpFrames() {
  const ex = frameExtractor;
  if (!ex || ex.busy) return;
  const t = ex.queue.shift();
  if (t == null) return;
  const key = `${ex.url}|${t}`;
  if (frameCache.has(key) || brokenFrameUrls.has(ex.url)) { pumpFrames(); return; }
  ex.busy = true;
  const done = () => { ex.busy = false; ex.listeners.forEach((l) => l()); pumpFrames(); };
  const onSeeked = () => {
    ex.video.removeEventListener("seeked", onSeeked);
    try {
      const c = document.createElement("canvas");
      c.width = 160; c.height = 90;
      const ctx = c.getContext("2d");
      if (ctx) {
        ctx.drawImage(ex.video, 0, 0, 160, 90);
        frameCache.set(key, c.toDataURL("image/jpeg", 0.6));
      }
    } catch (e) {
      brokenFrameUrls.add(ex.url); // canvas tainted → poster fallback everywhere
      ex.queue.length = 0;
    }
    done();
  };
  ex.video.addEventListener("seeked", onSeeked);
  try { ex.video.currentTime = t; } catch (e) { ex.video.removeEventListener("seeked", onSeeked); done(); }
}

function requestFrames(url: string, times: number[], listener: () => void): () => void {
  if (!url || brokenFrameUrls.has(url)) return () => undefined;
  if (!frameExtractor || frameExtractor.url !== url) {
    if (frameExtractor) { try { frameExtractor.video.src = ""; } catch (e) { /* ignore */ } }
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.preload = "auto";
    video.src = url;
    video.addEventListener("error", () => { if (frameExtractor && frameExtractor.url === url) brokenFrameUrls.add(url); });
    frameExtractor = { url, video, queue: [], busy: false, listeners: new Set() };
  }
  const ex = frameExtractor;
  ex.listeners.add(listener);
  times.forEach((t) => {
    const g = gridT(t);
    if (!frameCache.has(`${url}|${g}`) && !ex.queue.includes(g)) ex.queue.push(g);
  });
  if (ex.video.readyState >= 1) pumpFrames();
  else ex.video.addEventListener("loadedmetadata", () => pumpFrames(), { once: true });
  return () => { ex.listeners.delete(listener); };
}

/** One clip's strip of real frames: ~1 tile per second (≤8), poster-repeat fallback. */
const FrameTiles: React.FC<{ url: string; start: number; end: number; poster: string }> = ({ url, start, end, poster }) => {
  const [, force] = useState(0);
  const d = Math.max(0.1, end - start);
  const tiles = d > 1 ? Math.min(8, Math.floor(d)) : 1;
  const t0 = gridT(start);
  const t1 = gridT(end);
  const times = useMemo(
    () => Array.from({ length: tiles }, (_, k) => gridT(start + ((k + 0.5) * d) / tiles)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tiles, t0, t1]
  );
  useEffect(() => {
    if (!url || brokenFrameUrls.has(url)) return undefined;
    return requestFrames(url, times, () => force((x) => x + 1));
  }, [url, times]);

  if (!url || brokenFrameUrls.has(url)) {
    return (
      <Box sx={{ position: "absolute", inset: 0, backgroundImage: poster ? `url(${poster})` : "none", backgroundSize: "auto 100%", backgroundRepeat: "repeat-x", backgroundPosition: "left center" }} />
    );
  }
  return (
    <Box sx={{ position: "absolute", inset: 0, display: "flex" }}>
      {times.map((t, i) => {
        const f = frameCache.get(`${url}|${t}`);
        return (
          <Box key={i} sx={{ flex: 1, minWidth: 0, backgroundImage: f ? `url(${f})` : poster ? `url(${poster})` : "none", backgroundSize: "cover", backgroundPosition: "center", backgroundColor: "#101017", borderRight: i < times.length - 1 ? "1px solid rgba(0,0,0,0.45)" : "none" }} />
        );
      })}
    </Box>
  );
};

const SPEAKER_HUES: Record<string, string> = {};
const speakerColor = (speaker: string): string => {
  if (!SPEAKER_HUES[speaker]) {
    const palette = ["#4f8df9", "#2fbf8f", "#c77dff", "#f2a33c", "#39c0d4", "#e6659a"];
    SPEAKER_HUES[speaker] = palette[Object.keys(SPEAKER_HUES).length % palette.length];
  }
  return SPEAKER_HUES[speaker];
};

// NOTE: the bridge sends start/end in SECONDS (verified live against the local share),
// despite the row model docs calling them ms. All timeline math here is in seconds.
const fmtTime = (sec: number): string => {
  const s = Math.max(0, Math.round(sec));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};
const dur = (p: Pick): number => (p.sec && p.sec > 0 ? p.sec : Math.max(0, p.end - p.start));
const idNum = (id: ClipId): number => (typeof id === "number" ? id : id.split("").reduce((a, c) => a + c.charCodeAt(0), 0));

/* ══════════════════════════ THE CUT PLAYER ══════════════════════════ */
/** Plays THE CUT: two stacked <video> elements on the shared source, double-buffered — while
 *  one plays clip N, the hidden one is already parked on clip N+1's first frame, and a 60fps
 *  rAF engine (not the ~4Hz timeupdate event) swaps them at the boundary. That's what makes
 *  non-chronological cuts play as if they were one file. Appears only once clips are picked;
 *  docks at the right end of the timeline, pops out to a draggable window. */
const fmtSec1 = (s: number): string => {
  const m = Math.floor(Math.max(0, s) / 60);
  const r = Math.max(0, s) - m * 60;
  return `${String(m).padStart(2, "0")}:${r < 10 ? "0" : ""}${r.toFixed(1)}`;
};
const offsFor = (P: Pick[], i: number): number => P.slice(0, i).reduce((a, p) => a + dur(p), 0);

type PlayerProgress = { playingId: ClipId | null; clipT: number; globalT: number; playing: boolean };

const CutPlayer: React.FC<{
  picks: Pick[];
  videoUrl: string;
  seekReq: { id: ClipId; nonce: number } | null;
  onProgress: (p: PlayerProgress) => void;
  floating: boolean;
  onToggleFloat: () => void;
  music: { url: string; vol: number };
}> = ({ picks, videoUrl, seekReq, onProgress, floating, onToggleFloat, music }) => {
  const vARef = useRef<HTMLVideoElement | null>(null);
  const musicRef = useRef<HTMLAudioElement | null>(null);
  const vBRef = useRef<HTMLVideoElement | null>(null);
  const [face, setFace] = useState<0 | 1>(0); // which buffer is on screen
  const [currentId, setCurrentId] = useState<ClipId | null>(null);
  const [playing, setPlaying] = useState(false);
  const [blocked, setBlocked] = useState(false); // autoplay refused → ask for a tap
  const [clock, setClock] = useState(0); // cut-relative seconds, throttled
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastReportRef = useRef(0);
  // live mirror so the 60fps loop never reads a stale closure
  const liveRef = useRef<{ picks: Pick[]; idx: number; playing: boolean; face: 0 | 1 }>({ picks: [], idx: 0, playing: false, face: 0 });

  const idx = Math.max(0, picks.findIndex((p) => p.id === currentId));
  const clip = picks.length ? picks[Math.min(idx, picks.length - 1)] : null;
  const total = picks.reduce((a, p) => a + dur(p), 0);
  liveRef.current = { picks, idx, playing, face };

  const elOf = (f: 0 | 1) => (f === 0 ? vARef.current : vBRef.current);
  const seekEl = (v: HTMLVideoElement | null, t: number) => { if (v) { try { v.currentTime = t; } catch (e) { /* ignore */ } } };

  const report = (globalT: number, playingId: ClipId | null, isPlaying: boolean, clipT: number, force = false) => {
    const now = performance.now();
    if (!force && now - lastReportRef.current < 100) return;
    lastReportRef.current = now;
    setClock(globalT);
    onProgress({ playingId, clipT, globalT, playing: isPlaying });
  };

  // keep the standby buffer parked on the NEXT clip's first frame
  useEffect(() => {
    const nx = picks[idx + 1];
    const sb = elOf(face === 0 ? 1 : 0);
    if (sb && nx && Math.abs(sb.currentTime - nx.start) > 0.05) { sb.pause(); seekEl(sb, nx.start); }
  }, [picks, idx, face]);

  // keep a sane current clip when picks change (removals, reorders)
  useEffect(() => {
    if (!picks.length) { setCurrentId(null); setPlaying(false); liveRef.current.playing = false; return; }
    if (currentId == null || !picks.some((p) => p.id === currentId)) {
      setCurrentId(picks[0].id);
      seekEl(elOf(face), picks[0].start);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picks]);

  // clicking a clip in the timeline seeks the player there
  useEffect(() => {
    if (!seekReq) return;
    const p = picks.find((x) => x.id === seekReq.id);
    if (!p) return;
    setCurrentId(p.id);
    seekEl(elOf(liveRef.current.face), p.start);
    report(offsFor(picks, picks.findIndex((x) => x.id === p.id)), p.id, liveRef.current.playing, 0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seekReq]);

  // ── the boundary engine: 60fps, double-buffered swap at each clip's OUT ──
  const loop = () => {
    const { picks: P, idx: i, playing: pl, face: f } = liveRef.current;
    if (!pl) { rafRef.current = null; return; }
    const v = elOf(f);
    const c = P[i];
    if (v && c) {
      if (v.currentTime >= c.end - 0.02 || v.ended) {
        const nx = P[i + 1];
        if (nx) {
          const sb = elOf(f === 0 ? 1 : 0);
          v.pause();
          if (sb) {
            if (Math.abs(sb.currentTime - nx.start) > 0.25) seekEl(sb, nx.start);
            sb.play().catch(() => { /* ignore */ });
          }
          const nf = f === 0 ? 1 : 0;
          liveRef.current = { ...liveRef.current, face: nf, idx: i + 1 };
          setFace(nf);
          setCurrentId(nx.id);
          report(offsFor(P, i + 1), nx.id, true, 0, true);
        } else {
          v.pause();
          const bg = musicRef.current;
          if (bg) bg.pause();
          liveRef.current.playing = false;
          setPlaying(false);
          report(total, c.id, false, dur(c), true);
          rafRef.current = null;
          return;
        }
      } else {
        const clipT = clamp(v.currentTime - c.start, 0, dur(c));
        report(offsFor(P, i) + clipT, c.id, true, clipT);
      }
    }
    rafRef.current = requestAnimationFrame(loop);
  };

  const play = () => {
    const v = elOf(liveRef.current.face);
    if (!v || !clip) return;
    if (v.currentTime < clip.start - 0.2 || v.currentTime > clip.end - 0.05) seekEl(v, clip.start);
    v.play().then(() => {
      setBlocked(false);
      setPlaying(true);
      liveRef.current.playing = true;
      if (music.url && musicRef.current) { musicRef.current.volume = clamp(music.vol, 0, 1); musicRef.current.play().catch(() => { /* ignore */ }); }
      if (!rafRef.current) rafRef.current = requestAnimationFrame(loop);
    }).catch(() => setBlocked(true));
  };
  const pause = () => {
    const v = elOf(liveRef.current.face);
    if (v) v.pause();
    if (musicRef.current) musicRef.current.pause();
    setPlaying(false);
    liveRef.current.playing = false;
  };
  const step = (d: 1 | -1) => {
    const ni = clamp(idx + d, 0, picks.length - 1);
    const p = picks[ni];
    if (!p) return;
    setCurrentId(p.id);
    seekEl(elOf(liveRef.current.face), p.start);
    report(offsFor(picks, ni), p.id, liveRef.current.playing, 0, true);
  };
  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  // M1 music bed: loops under the cut in PREVIEW (exports don't mix music yet).
  useEffect(() => { if (musicRef.current) musicRef.current.volume = clamp(music.vol, 0, 1); }, [music.vol]);
  useEffect(() => {
    const a = musicRef.current;
    if (!a) return;
    if (!music.url) { a.pause(); return; }
    if (liveRef.current.playing) { a.play().catch(() => { /* ignore */ }); }
  }, [music.url]);

  // draggable when floating
  const onDragStart = (e: React.PointerEvent) => {
    if (!floating) return;
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
    const move = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      setPos({ x: d.ox + (ev.clientX - d.sx), y: d.oy + (ev.clientY - d.sy) });
    };
    const up = () => { dragRef.current = null; window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  if (!picks.length || !videoUrl) return null;

  const card = (
    <Box sx={{ display: "flex", flexDirection: "column", borderRadius: "10px", overflow: "hidden", border: `1px solid ${floating ? "rgba(230,29,80,0.55)" : "rgba(255,255,255,0.14)"}`, background: "#08070b", boxShadow: floating ? "0 24px 70px -20px rgba(0,0,0,0.95)" : "none", width: floating ? { xs: "300px", md: "360px" } : "100%", pointerEvents: "auto" }}>
      {/* header — drag handle when floating */}
      <Box onPointerDown={onDragStart}
        sx={{ display: "flex", alignItems: "center", gap: "8px", px: "10px", py: "6px", borderBottom: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)", cursor: floating ? "grab" : "default", userSelect: "none" }}>
        <Box aria-hidden sx={{ width: 6, height: 6, borderRadius: "50%", background: CHERRY }} />
        <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "11.5px", color: "#f3f4f9" }}>Your cut — preview</Typography>
        <Box sx={{ flex: 1 }} />
        {floating && <Typography sx={{ fontFamily: font, fontSize: "10px", color: "#8d94a8" }}>drag me</Typography>}
        <Box component="button" onClick={onToggleFloat} title={floating ? "Dock back into the timeline" : "Pop out — move it anywhere"}
          sx={{ cursor: "pointer", background: "transparent", border: "1px solid rgba(255,255,255,0.16)", borderRadius: "6px", color: "#c2c7d6", fontFamily: font, fontSize: "10.5px", px: "7px", py: "3px", "&:hover": { color: "#fff", borderColor: "rgba(255,255,255,0.35)" } }}>
          {floating ? "⇲ dock" : "⇱ pop out"}
        </Box>
      </Box>
      {/* the double-buffered video pair */}
      <Box sx={{ position: "relative", background: "#000", aspectRatio: "16 / 9" }}>
        {music.url ? <audio ref={musicRef} src={music.url} loop preload="auto" /> : null}
        {([0, 1] as const).map((f) => (
          <video
            key={f}
            ref={f === 0 ? vARef : vBRef}
            src={videoUrl}
            playsInline
            preload="auto"
            onClick={() => (playing ? pause() : play())}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", cursor: "pointer", opacity: face === f ? 1 : 0 }}
          />
        ))}
        {!playing && (
          <Box onClick={play} sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", background: "rgba(0,0,0,0.25)" }}>
            <Box sx={{ width: 46, height: 46, borderRadius: "50%", background: "rgba(230,29,80,0.92)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 26px -8px rgba(230,29,80,0.8)" }}>
              <Typography sx={{ color: "#fff", fontSize: "18px", ml: "3px" }}>▶</Typography>
            </Box>
          </Box>
        )}
        {blocked && (
          <Typography sx={{ position: "absolute", bottom: 6, left: 0, right: 0, textAlign: "center", fontFamily: font, fontSize: "10.5px", color: "#f4c9d6" }}>
            tap ▶ to start (browser blocked autoplay)
          </Typography>
        )}
      </Box>
      {/* transport */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", px: "8px", py: "6px" }}>
        {[
          { t: "⏮", fn: () => step(-1), title: "Previous clip" },
          { t: playing ? "⏸" : "▶", fn: () => (playing ? pause() : play()), title: playing ? "Pause" : "Play the cut" },
          { t: "⏭", fn: () => step(1), title: "Next clip" },
        ].map((b) => (
          <Box key={b.title} component="button" onClick={b.fn} title={b.title}
            sx={{ cursor: "pointer", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "6px", color: "#fff", fontFamily: font, fontSize: "12px", px: "9px", py: "4px", lineHeight: 1, "&:hover": { background: "rgba(230,29,80,0.25)" } }}>
            {b.t}
          </Box>
        ))}
        <Typography sx={{ fontFamily: mono, fontSize: "10px", color: "#8d94a8", ml: "2px", whiteSpace: "nowrap" }}>
          clip {Math.min(idx + 1, picks.length)}/{picks.length}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontFamily: mono, fontSize: "10px", color: "#8d94a8", whiteSpace: "nowrap" }}>
          {fmtSec1(clock)} / {fmtSec1(total)}
        </Typography>
      </Box>
    </Box>
  );

  if (floating) {
    return (
      <Box sx={{ position: "fixed", zIndex: 12500, left: { xs: "12px", md: "auto" }, right: { md: "36px" }, bottom: "88px", transform: `translate(${pos.x}px, ${pos.y}px)`, pointerEvents: "none" }}>
        {card}
      </Box>
    );
  }
  return card;
};

/* ══════════════════════════ THE CHAT DOCK ══════════════════════════ */
/** A real chat input — the familiar ChatGPT/Claude pill — sitting right under the editor,
 *  where the Select-clips / Export pill lives. It IS Black Cherry: messages go over the
 *  bridge into the ET's own chat pipeline (guest AI on shares, your key when logged in),
 *  the AI's picks tick themselves in the sheet and land on the timeline, and the reply
 *  comes back here. One brain, two doorways. */
const ChatDock: React.FC<{ thread: ChatMsg[]; busy: boolean; onSend: (text: string) => void }> = ({ thread, busy, onSend }) => {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(true); // the conversation panel can be collapsed to just the pill
  const lastLenRef = useRef(0);
  const threadRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread, busy, open]);
  // a NEW message re-opens the panel (a manual close stays closed until then)
  useEffect(() => {
    if (thread.length > lastLenRef.current) setOpen(true);
    lastLenRef.current = thread.length;
  }, [thread.length]);
  const send = () => {
    const t = text.trim();
    if (!t || busy) return;
    onSend(t);
    setText("");
  };
  return (
    <Box sx={{ width: "100%", maxWidth: "820px", mx: "auto", display: "flex", flexDirection: "column", gap: "8px" }}>
      {/* header — makes it unmistakably a CHAT with the AI, not just an input box */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
        <Box aria-hidden sx={{ width: 30, height: 30, borderRadius: "50%", background: "#160c26", border: "1px solid rgba(139,92,246,0.55)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <img src={logoPurple} alt="" height={16} style={{ width: "auto" }} />
        </Box>
        <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: "15px", color: "#efeaff" }}>
          Black Cherry
          <Box component="span" sx={{ fontSize: "10px", fontWeight: 800, color: "#fff", background: "#7c3aed", px: "6px", py: "2px", borderRadius: "5px", ml: "6px", verticalAlign: "middle" }}>AI</Box>
        </Typography>
        <Typography sx={{ fontFamily: font, fontSize: "12px", color: "#9aa0b4", display: { xs: "none", sm: "block" } }}>
          — tell it what you want; it picks, orders &amp; scores the clips for you
        </Typography>
      </Box>
      {/* the conversation — appears once there's something to show; collapsible */}
      {(thread.length > 0 || busy) && open && (
        <Box sx={{ position: "relative" }}>
          <Box component="button" onClick={() => setOpen(false)} title="Hide the conversation (the chat pill stays)"
            sx={{ position: "absolute", top: "-9px", right: "8px", zIndex: 1, cursor: "pointer", px: "8px", py: "2px", borderRadius: "999px", border: "1px solid rgba(255,255,255,0.18)", background: "#0d0c12", color: "#8d94a8", fontFamily: font, fontSize: "10px", fontWeight: 700, lineHeight: 1.4, "&:hover": { color: "#fff", borderColor: "rgba(230,29,80,0.6)" } }}>
            ✕ hide
          </Box>
        <Box ref={threadRef} sx={{ maxHeight: "150px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "6px", p: "8px", borderRadius: "12px", border: "1px solid rgba(255,255,255,0.09)", background: "rgba(8,7,11,0.6)" }}>
          {thread.map((m, i) => (
            <Box key={i} sx={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
              <Box sx={{ maxWidth: "86%", px: "10px", py: "6px", borderRadius: m.role === "user" ? "12px 12px 3px 12px" : "12px 12px 12px 3px", background: m.role === "user" ? "rgba(230,29,80,0.18)" : "rgba(255,255,255,0.05)", border: `1px solid ${m.role === "user" ? "rgba(230,29,80,0.4)" : "rgba(255,255,255,0.1)"}` }}>
                <Typography sx={{ fontFamily: font, fontSize: "12px", lineHeight: 1.5, color: "#e6e8f0", whiteSpace: "pre-wrap" }}>
                  {m.role === "assistant" && <Box component="span" aria-hidden sx={{ mr: "5px" }}>🍒</Box>}
                  {m.text}
                </Typography>
                {m.role === "assistant" && (m.applied || 0) > 0 && (
                  <Typography sx={{ fontFamily: mono, fontSize: "10px", color: "#f4c9d6", mt: "3px" }}>
                    ✓ {m.applied} clip{m.applied === 1 ? "" : "s"} ticked — they're on the timeline below
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
          {busy && (
            <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: "#9aa0b4", px: "4px" }}>
              🍒 Black Cherry is cutting<Box component="span" sx={{ "@keyframes ccDots": { "0%": { opacity: 0.2 }, "50%": { opacity: 1 }, "100%": { opacity: 0.2 } }, animation: "ccDots 1.2s infinite" }}>…</Box>
            </Typography>
          )}
        </Box>
        </Box>
      )}
      {thread.length > 0 && !open && (
        <Box component="button" onClick={() => setOpen(true)}
          sx={{ alignSelf: "center", cursor: "pointer", px: "10px", py: "3px", borderRadius: "999px", border: "1px solid rgba(255,255,255,0.14)", background: "transparent", color: "#8d94a8", fontFamily: font, fontSize: "10.5px", fontWeight: 700, "&:hover": { color: "#fff", borderColor: "rgba(230,29,80,0.6)" } }}>
          show conversation ({thread.length})
        </Box>
      )}
      {/* the pill — the Black Cherry chat box (deliberately large; it's a primary action) */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", px: "16px", py: "13px", borderRadius: "18px", border: "1.5px solid rgba(139,92,246,0.45)", background: "#0d0c12", boxShadow: "0 16px 40px -20px rgba(0,0,0,0.9), 0 0 0 1px rgba(139,92,246,0.12)", "&:focus-within": { borderColor: "rgba(139,92,246,0.8)" } }}>
        <Box aria-hidden sx={{ flexShrink: 0, width: 42, height: 42, borderRadius: "50%", background: "#160c26", border: "1px solid rgba(139,92,246,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img src={logoPurple} alt="" height={22} style={{ width: "auto" }} />
        </Box>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder='Ask Black Cherry — “pick the 3 strongest moments and put the hook first”'
          style={{ flex: 1, minWidth: 0, background: "transparent", border: "none", outline: "none", color: "#e6e8f0", fontFamily: "Outfit, sans-serif", fontSize: "16px", padding: "8px 2px" }}
        />
        <Box component="button" onClick={send} aria-label="Send" title={busy ? "Black Cherry is thinking…" : "Send"}
          sx={{ flexShrink: 0, cursor: busy || !text.trim() ? "default" : "pointer", width: 44, height: 44, borderRadius: "50%", border: "none", background: busy || !text.trim() ? "rgba(255,255,255,0.08)" : CHERRY, color: "#fff", fontSize: "19px", lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", transition: "background .15s", "&:hover": { background: busy || !text.trim() ? "rgba(255,255,255,0.08)" : "#c81834" } }}>
          ➤
        </Box>
      </Box>
    </Box>
  );
};

/* ══════════════════════════ THE TIMELINE ══════════════════════════ */
/** The editing surface under the spreadsheet. Two honest views of the same cut:
 *  ▦ Storyboard — every pick as an equal card, in pick order (calm, scannable);
 *  ⣿ Timeline  — Premiere/CapCut-style: width = true duration, filmstrip posters,
 *                and GRAB-THE-ENDS trimming: drag a selected clip's edge, and on release the
 *                whole gesture lands as one batched ±0.1s trim in the sheet (one undo).
 *  The clip tools live in the header row (next to the view toggle) so the tracks get the space.
 *  Every edit round-trips through the ET — the sheet and the timeline never disagree. */
const EditTimeline: React.FC<{
  picks: Pick[];
  videoUrl: string;
  sendCmd: SendCmd;
}> = ({ picks, videoUrl, sendCmd }) => {
  const totalSec = picks.reduce((a, p) => a + dur(p), 0);
  const [mode, setMode] = useState<"storyboard" | "timeline">("timeline");
  const [selectedId, setSelectedId] = useState<ClipId | null>(null);
  const [seekReq, setSeekReq] = useState<{ id: ClipId; nonce: number } | null>(null);
  const [floating, setFloating] = useState(false);
  const [prog, setProg] = useState<PlayerProgress>({ playingId: null, clipT: 0, globalT: 0, playing: false });
  // M1 music bed (preview-only): tracks come from /music/manifest.json — drop your own
  // instrumentals (e.g. downloaded from your Suno library) into emofront/public/music/.
  const [musicList, setMusicList] = useState<{ file: string; title: string; description?: string; vocal?: boolean }[]>([]);
  const [musicUrl, setMusicUrl] = useState("");
  const [musicVol, setMusicVol] = useState(0.25);
  useEffect(() => {
    fetch("/music/manifest.json")
      .then(async (r) => (r.ok ? r.json() : []))
      .then((j) => { if (Array.isArray(j)) setMusicList(j.filter((t) => t && t.file)); })
      .catch(() => { /* no library — the picker still offers "your own file" */ });
  }, []);
  // drag-trim: optimistic preview in 0.1s steps until the ET echoes the authoritative times
  const [preview, setPreview] = useState<{ id: ClipId; dIn: number; dOut: number } | null>(null);
  const seekNonce = useRef(0);
  const trackRef = useRef<HTMLDivElement | null>(null);

  // a removed clip can't stay selected; fresh picks supersede any drag preview
  useEffect(() => {
    if (selectedId != null && !picks.some((p) => p.id === selectedId)) setSelectedId(null);
    setPreview(null);
  }, [picks, selectedId]);

  const eff = (p: Pick): { start: number; end: number } =>
    preview && preview.id === p.id
      ? { start: p.start + preview.dIn * 0.1, end: p.end + preview.dOut * 0.1 }
      : { start: p.start, end: p.end };
  const effDur = (p: Pick): number => { const e = eff(p); return Math.max(0.1, e.end - e.start); };

  // Ruler ticks across the assembled edit (not the source video — this IS your cut).
  const ticks = useMemo(() => {
    if (!totalSec) return [];
    const step = totalSec > 120 ? 30 : totalSec > 30 ? 10 : totalSec > 8 ? 5 : 1;
    const out: { at: number; label: string }[] = [];
    for (let t = 0; t <= totalSec; t += step) out.push({ at: (t / totalSec) * 100, label: fmtTime(t) });
    return out;
  }, [totalSec]);

  const sel = selectedId != null ? picks.find((p) => p.id === selectedId) || null : null;
  const selectClip = (p: Pick) => {
    if (selectedId === p.id) { setSelectedId(null); return; } // click again = done editing
    setSelectedId(p.id);
    seekNonce.current += 1;
    setSeekReq({ id: p.id, nonce: seekNonce.current });
  };
  // Esc = done editing (nothing is lost — trims are already applied & undoable in the sheet)
  useEffect(() => {
    if (selectedId == null) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setSelectedId(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId]);
  const move = (dir: 1 | -1) => {
    if (!sel) return;
    const i = picks.findIndex((p) => p.id === sel.id);
    const j = i + dir;
    if (i === -1 || j < 0 || j >= picks.length) return;
    const ids = picks.map((p) => p.id);
    const tmp = ids[i]; ids[i] = ids[j]; ids[j] = tmp;
    sendCmd({ cmd: "reorder", ids });
  };
  const removeSel = () => { if (sel) { sendCmd({ cmd: "deselect", id: sel.id }); setSelectedId(null); } };
  // ✂ split at the playhead if it's inside this clip, else at the clip's middle. The ET
  // snaps to the nearest word boundary (same rule as right-click-a-word in the sheet).
  const splitSel = () => {
    if (!sel) return;
    const t = prog.playingId === sel.id ? sel.start + prog.clipT : (sel.start + sel.end) / 2;
    sendCmd({ cmd: "split", id: sel.id, t });
    setSelectedId(null);
  };
  const adjust = (which: "in" | "out", steps: number) => {
    if (!sel || !steps) return;
    sendCmd({
      cmd: "adjust",
      id: sel.id,
      deltaStart: which === "in" ? steps : 0,
      deltaEnd: which === "out" ? steps : 0,
    });
  };

  // ── grab-the-ends trimming (Timeline view): drag an edge, release = one batched trim ──
  const onHandleDown = (e: React.PointerEvent, p: Pick, side: "in" | "out") => {
    e.stopPropagation();
    e.preventDefault();
    if (selectedId !== p.id) selectClip(p);
    const track = trackRef.current;
    if (!track || !totalSec) return;
    const pxPerSec = track.getBoundingClientRect().width / totalSec;
    if (pxPerSec <= 0) return;
    const startX = e.clientX;
    const maxShrink = Math.floor((dur(p) - 0.2) / 0.1); // keep ≥0.2s of clip
    let steps = 0;
    const onMove = (ev: PointerEvent) => {
      const dSec = (ev.clientX - startX) / pxPerSec;
      let s = Math.round(dSec / 0.1);
      if (side === "in") s = clamp(s, -600, Math.max(0, maxShrink));       // IN → right shrinks
      else s = clamp(s, -Math.max(0, maxShrink), 600);                      // OUT → left shrinks
      if (s !== steps) {
        steps = s;
        setPreview({ id: p.id, dIn: side === "in" ? steps : 0, dOut: side === "out" ? steps : 0 });
      }
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      if (steps !== 0) {
        sendCmd({
          cmd: "adjust",
          id: p.id,
          deltaStart: side === "in" ? steps : 0,
          deltaEnd: side === "out" ? steps : 0,
        });
      } else {
        setPreview(null);
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const playheadPct = totalSec ? clamp((prog.globalT / totalSec) * 100, 0, 100) : 0;

  const modeBtn = (m: "storyboard" | "timeline", label: string, hint: string) => {
    const on = mode === m;
    return (
      <Box component="button" key={m} onClick={() => setMode(m)} title={hint}
        sx={{ cursor: "pointer", fontFamily: font, fontWeight: 700, fontSize: "11px", px: "10px", py: "4px", border: "none", background: on ? "linear-gradient(100deg,#e61d50,#b3123c)" : "transparent", color: on ? "#fff" : "#9aa0b4", "&:hover": { color: "#fff" } }}>
        {label}
      </Box>
    );
  };

  const trimBtn = (label: string, title: string, fn: () => void) => (
    <Box component="button" key={title} onClick={fn} title={title}
      sx={{ cursor: "pointer", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "6px", color: "#e6e8f0", fontFamily: mono, fontSize: "11px", fontWeight: 700, px: "8px", py: "4px", lineHeight: 1, "&:hover": { borderColor: CHERRY, color: "#fff" } }}>
      {label}
    </Box>
  );

  return (
    <Box sx={{ borderRadius: "0 0 14px 14px", border: "1px solid rgba(255,255,255,0.12)", borderTop: "none", background: "#0d0c12", overflow: "hidden" }}>
      {/* header bar — title · counts · view toggle · THE CLIP TOOLS (so the tracks keep the space) */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", px: "13px", py: "6px", borderBottom: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.02)", flexWrap: "wrap", minHeight: "38px" }}>
        <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "12.5px", color: "#f3f4f9" }}>Timeline — your cut</Typography>
        <Typography sx={{ fontFamily: mono, fontSize: "11.5px", color: "#8d94a8" }}>
          {picks.length ? `${picks.length} clip${picks.length === 1 ? "" : "s"} · ${fmtTime(totalSec)}` : "empty"}
        </Typography>
        <Box sx={{ display: "inline-flex", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "999px", overflow: "hidden" }}>
          {modeBtn("storyboard", "▦ Storyboard", "Equal cards, in pick order — plays in this order")}
          {modeBtn("timeline", "⣿ Timeline", "True durations — grab a selected clip's ends to trim")}
        </Box>
        <Box sx={{ flex: 1 }} />
        {sel ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: "7px", flexWrap: "wrap" }}>
            <Typography sx={{ fontFamily: mono, fontSize: "10.5px", color: "#f4c9d6", whiteSpace: "nowrap" }}>
              #{sel.order} · {sel.speaker || "?"}
            </Typography>
            <Typography sx={{ fontFamily: mono, fontSize: "10.5px", color: "#8d94a8", whiteSpace: "nowrap", display: { xs: "none", sm: "block" } }}>
              {fmtSec1(eff(sel).start)} → {fmtSec1(eff(sel).end)} ({effDur(sel).toFixed(1)}s)
            </Typography>
            <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 700, color: "#8d94a8" }}>IN</Typography>
            {trimBtn("−", "Start 0.1s earlier (steals from the previous clip)", () => adjust("in", -1))}
            {trimBtn("+", "Start 0.1s later", () => adjust("in", 1))}
            <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 700, color: "#8d94a8" }}>OUT</Typography>
            {trimBtn("−", "End 0.1s earlier", () => adjust("out", -1))}
            {trimBtn("+", "End 0.1s later (the next clip gives way)", () => adjust("out", 1))}
            {trimBtn("◀", "Move earlier in the cut", () => move(-1))}
            {trimBtn("▶", "Move later in the cut", () => move(1))}
            {trimBtn("✂", "Split at the playhead (nearest word) — both halves stay picked", splitSel)}
            {trimBtn("✕", "Remove from the cut (unticks it in the sheet)", removeSel)}
            <Box component="button" onClick={() => setSelectedId(null)} title="Done editing (Esc, or click the clip again). Your trims are saved — nothing to lose."
              sx={{ cursor: "pointer", background: "rgba(40,200,120,0.12)", border: "1px solid rgba(40,200,120,0.5)", borderRadius: "6px", color: "#6de0a8", fontFamily: font, fontSize: "11px", fontWeight: 700, px: "9px", py: "4px", lineHeight: 1, "&:hover": { background: "rgba(40,200,120,0.22)" } }}>
              ✓ Done
            </Box>
          </Box>
        ) : (
          <Typography sx={{ fontFamily: font, fontSize: "11px", color: "#8d94a8", display: { xs: "none", md: "block" } }}>
            {picks.length ? "Click a clip to trim, reorder, or remove — every edit updates the sheet." : "Exports in this exact order — MP4, or EDL / Premiere XML"}
          </Typography>
        )}
      </Box>

      {picks.length === 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "8px", py: "34px", px: "16px" }}>
          <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "15px", color: "#c2c7d6", textAlign: "center" }}>
            <Box component="span" sx={{ color: CHERRY }}>↑</Box> Tick clips in the editor above — they land here, and the player appears.
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: "#8d94a8", textAlign: "center" }}>
            Cherry-pick by hand, or hit the blue <Box component="span" sx={{ color: "#9dbcff", fontWeight: 700 }}>Ask AI</Box> pill and let Black Cherry make the first pass.
          </Typography>
          {/* ghost track so the surface still reads as a timeline */}
          <Box sx={{ width: "100%", maxWidth: "860px", mt: "10px", display: "flex", gap: "4px", opacity: 0.22 }}>
            {[18, 9, 24, 13, 20, 16].map((w, i) => (
              <Box key={i} sx={{ flex: `0 0 ${w}%`, height: 44, borderRadius: "6px", border: "1px dashed rgba(255,255,255,0.35)" }} />
            ))}
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: "12px", px: "13px", py: "12px" }}>
          {/* ── LEFT: the tracks ── */}
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            {mode === "timeline" && (
              <Box sx={{ position: "relative", height: "18px", ml: "46px", mb: "4px" }}>
                {ticks.map((t, i) => (
                  <Box key={i} sx={{ position: "absolute", left: `${t.at}%`, top: 0, transform: "translateX(-50%)" }}>
                    <Typography sx={{ fontFamily: mono, fontSize: "9.5px", color: "#6e7488", whiteSpace: "nowrap" }}>{t.label}</Typography>
                  </Box>
                ))}
              </Box>
            )}

            {mode === "storyboard" ? (
              /* ▦ STORYBOARD — every pick an equal card, in pick order */
              <Box sx={{ display: "flex", gap: "8px", overflowX: "auto", pb: "6px" }}>
                {picks.map((p) => {
                  const isSel = selectedId === p.id;
                  const isPlaying = prog.playingId === p.id;
                  return (
                    <Box key={p.id} onClick={() => selectClip(p)} title={`#${p.order} · ${p.speaker || "?"} · ${p.sentence}`}
                      sx={{ flex: "0 0 150px", cursor: "pointer", borderRadius: "8px", overflow: "hidden", border: `2px solid ${isSel ? CHERRY : isPlaying ? "#ff456a88" : "rgba(255,255,255,0.1)"}`, background: "#101017", transition: "border-color .12s, transform .12s", "&:hover": { transform: "translateY(-2px)" } }}>
                      <Box sx={{ position: "relative", height: "84px", background: p.thumb ? `url(${p.thumb}) center/cover` : `linear-gradient(180deg, ${speakerColor(p.speaker)}33, ${speakerColor(p.speaker)}14)` }}>
                        <Box sx={{ position: "absolute", top: 4, left: 4, minWidth: 17, height: 17, px: "3px", borderRadius: "4px", background: CHERRY, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography sx={{ fontFamily: mono, fontSize: "10px", fontWeight: 700, color: "#fff", lineHeight: 1 }}>{p.order}</Typography>
                        </Box>
                        {p.trimmed && (
                          <Box title="Trimmed — synced with the sheet" sx={{ position: "absolute", top: 4, right: 4, px: "4px", py: "1px", borderRadius: "4px", background: "rgba(20,184,166,0.85)" }}>
                            <Typography sx={{ fontFamily: mono, fontSize: "9px", fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>✂</Typography>
                          </Box>
                        )}
                        <Box sx={{ position: "absolute", right: 4, bottom: 4, px: "4px", py: "1px", borderRadius: "4px", background: "rgba(8,7,11,0.75)" }}>
                          <Typography sx={{ fontFamily: mono, fontSize: "9.5px", color: "#e6e8f0", lineHeight: 1.3 }}>{dur(p).toFixed(1)}s</Typography>
                        </Box>
                        {/* live progress under the playing card */}
                        {isPlaying && (
                          <Box sx={{ position: "absolute", left: 0, bottom: 0, height: "3px", width: `${clamp((prog.clipT / Math.max(0.1, dur(p))) * 100, 0, 100)}%`, background: "#ff456a" }} />
                        )}
                      </Box>
                      <Box sx={{ px: "7px", py: "5px", borderTop: `2px solid ${speakerColor(p.speaker)}55` }}>
                        <Typography noWrap sx={{ fontFamily: font, fontSize: "10px", color: "#c2c7d6" }}>{p.sentence}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              /* ⣿ TIMELINE — true durations, filmstrip posters, grab-the-ends trimming */
              <>
                <Box sx={{ display: "flex", alignItems: "stretch", gap: "8px" }}>
                  <Box sx={{ flex: "0 0 38px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "6px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                    <Typography sx={{ fontFamily: mono, fontSize: "10.5px", fontWeight: 700, color: "#8d94a8" }}>V1</Typography>
                  </Box>
                  <Box ref={trackRef} sx={{ position: "relative", flex: 1, minWidth: 0, display: "flex", gap: "3px", background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "8px", p: "4px", overflow: "hidden" }}>
                    {picks.map((p) => {
                      const w = totalSec ? (effDur(p) / totalSec) * 100 : 0;
                      const isSel = selectedId === p.id;
                      const dragging = preview && preview.id === p.id;
                      return (
                        <Box key={p.id} onClick={() => selectClip(p)} title={`#${p.order} · ${p.speaker || "?"} · ${p.sentence}`}
                          sx={{
                            position: "relative", flex: `0 0 calc(${w}% - 3px)`, minWidth: "30px", height: 64,
                            borderRadius: "6px", overflow: "hidden", cursor: "pointer",
                            border: `2px solid ${dragging ? "#ff456a" : isSel ? CHERRY : `${speakerColor(p.speaker)}66`}`,
                            background: `linear-gradient(180deg, ${speakerColor(p.speaker)}33, ${speakerColor(p.speaker)}14)`,
                            transition: dragging ? "none" : "border-color .12s",
                            "&:hover": { borderColor: isSel ? CHERRY : "#ff456a88" },
                          }}
                        >
                          {/* REAL frames (~1/sec, extracted client-side; poster-repeat fallback) */}
                          <FrameTiles url={videoUrl} start={eff(p).start} end={eff(p).end} poster={p.thumb} />
                          <Box aria-hidden sx={{ position: "absolute", inset: 0, background: "linear-gradient(rgba(10,9,14,0.15), rgba(10,9,14,0.45))" }} />
                          <Box sx={{ position: "absolute", top: 3, left: 3, minWidth: 16, height: 16, px: "3px", borderRadius: "4px", background: CHERRY, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Typography sx={{ fontFamily: mono, fontSize: "9.5px", fontWeight: 700, color: "#fff", lineHeight: 1 }}>{p.order}</Typography>
                          </Box>
                          {p.trimmed && !isSel && (
                            <Box title="Trimmed — synced with the sheet" sx={{ position: "absolute", top: 3, right: 3, px: "4px", py: "1px", borderRadius: "4px", background: "rgba(20,184,166,0.85)" }}>
                              <Typography sx={{ fontFamily: mono, fontSize: "9px", fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>✂</Typography>
                            </Box>
                          )}
                          <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, px: "5px", py: "2px", background: "linear-gradient(transparent, rgba(8,7,11,0.85))" }}>
                            <Typography noWrap sx={{ fontFamily: font, fontSize: "9.5px", color: "#e6e8f0" }}>{p.sentence}</Typography>
                          </Box>
                          {/* grab-the-ends handles — CapCut style, on the selected clip */}
                          {isSel && (
                            <>
                              <Box onPointerDown={(e: React.PointerEvent) => onHandleDown(e, p, "in")} title="Drag to trim the start"
                                sx={{ position: "absolute", top: 0, bottom: 0, left: 0, width: "11px", cursor: "ew-resize", background: "linear-gradient(90deg, rgba(230,29,80,0.95), rgba(230,29,80,0.25))", display: "flex", alignItems: "center", justifyContent: "center", touchAction: "none" }}>
                                <Box sx={{ width: "2px", height: "22px", borderRadius: "2px", background: "#fff", opacity: 0.9 }} />
                              </Box>
                              <Box onPointerDown={(e: React.PointerEvent) => onHandleDown(e, p, "out")} title="Drag to trim the end"
                                sx={{ position: "absolute", top: 0, bottom: 0, right: 0, width: "11px", cursor: "ew-resize", background: "linear-gradient(270deg, rgba(230,29,80,0.95), rgba(230,29,80,0.25))", display: "flex", alignItems: "center", justifyContent: "center", touchAction: "none" }}>
                                <Box sx={{ width: "2px", height: "22px", borderRadius: "2px", background: "#fff", opacity: 0.9 }} />
                              </Box>
                            </>
                          )}
                          {/* live trim readout while dragging */}
                          {dragging && (
                            <Box sx={{ position: "absolute", top: 3, right: 3, px: "5px", py: "2px", borderRadius: "4px", background: "rgba(8,7,11,0.9)", border: "1px solid rgba(255,69,106,0.6)" }}>
                              <Typography sx={{ fontFamily: mono, fontSize: "9.5px", fontWeight: 700, color: "#ff9db1", lineHeight: 1.2 }}>
                                {effDur(p).toFixed(1)}s
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                    {/* playhead — driven by the real player, not an animation */}
                    {(prog.playing || prog.globalT > 0) && (
                      <Box aria-hidden sx={{ position: "absolute", top: 0, bottom: 0, left: `${playheadPct}%`, width: "2px", background: "#ff456a", boxShadow: "0 0 8px rgba(255,69,106,0.9)", pointerEvents: "none" }} />
                    )}
                  </Box>
                </Box>

                {/* A1 — audio strip (decorative bars under each clip, same widths) */}
                <Box sx={{ display: "flex", alignItems: "stretch", gap: "8px", mt: "5px" }}>
                  <Box sx={{ flex: "0 0 38px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "6px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                    <Typography sx={{ fontFamily: mono, fontSize: "10.5px", fontWeight: 700, color: "#8d94a8" }}>A1</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, display: "flex", gap: "3px", background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "8px", p: "4px" }}>
                    {picks.map((p) => {
                      const w = totalSec ? (effDur(p) / totalSec) * 100 : 0;
                      return (
                        <Box key={p.id} sx={{ flex: `0 0 calc(${w}% - 3px)`, minWidth: "30px", height: 20, borderRadius: "4px", overflow: "hidden", display: "flex", alignItems: "center", gap: "1.5px", px: "3px", background: `${speakerColor(p.speaker)}18`, border: `1px solid ${speakerColor(p.speaker)}40` }}>
                          {Array.from({ length: 24 }).map((_, i) => (
                            <Box key={i} sx={{ flex: 1, height: `${25 + ((idNum(p.id) * 7 + i * 13) % 60)}%`, borderRadius: "1px", background: `${speakerColor(p.speaker)}99` }} />
                          ))}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </>
            )}

            {/* ── M1: the music bed — your library first (Suno downloads welcome), preview-only ── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "6px", flexWrap: "wrap" }}>
              <Box sx={{ flex: "0 0 38px", alignSelf: "stretch", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "6px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <Typography sx={{ fontFamily: mono, fontSize: "10.5px", fontWeight: 700, color: "#8d94a8" }}>M1</Typography>
              </Box>
              <select
                value={musicUrl.startsWith("blob:") ? "__custom__" : musicUrl}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v !== "__custom__") setMusicUrl(v);
                }}
                style={{ background: "#0d0c12", color: "#c2c7d6", border: "1px solid rgba(255,255,255,0.16)", borderRadius: "6px", fontFamily: "Outfit, sans-serif", fontSize: "11.5px", padding: "5px 8px", maxWidth: "220px" }}
              >
                <option value="">No music</option>
                {musicList.filter((t) => !t.vocal).length > 0 && (
                  <optgroup label="🎹 Instrumentals">
                    {musicList.filter((t) => !t.vocal).map((t) => (
                      <option key={t.file} value={`/music/${t.file}`} title={t.description || ""}>{t.title || t.file}</option>
                    ))}
                  </optgroup>
                )}
                {musicList.filter((t) => t.vocal).length > 0 && (
                  <optgroup label="🎤 With vocals">
                    {musicList.filter((t) => t.vocal).map((t) => (
                      <option key={t.file} value={`/music/${t.file}`} title={t.description || ""}>🎤 {t.title || t.file}</option>
                    ))}
                  </optgroup>
                )}
                {musicUrl.startsWith("blob:") && <option value="__custom__">Your file</option>}
              </select>
              <Box component="label" sx={{ cursor: "pointer", px: "9px", py: "4px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.14)", color: "#c2c7d6", fontFamily: font, fontSize: "10.5px", "&:hover": { borderColor: CHERRY, color: "#fff" } }}>
                + your track
                <input type="file" accept="audio/*" style={{ display: "none" }}
                  onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) setMusicUrl(URL.createObjectURL(f)); }} />
              </Box>
              {musicUrl && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 700, color: "#8d94a8" }}>VOL</Typography>
                  <input type="range" min={0} max={100} value={Math.round(musicVol * 100)}
                    onChange={(e) => setMusicVol(parseInt(e.target.value, 10) / 100)}
                    style={{ width: "90px", accentColor: CHERRY, cursor: "pointer" }} />
                </Box>
              )}
              <Typography sx={{ fontFamily: font, fontSize: "10px", color: "#6e7488" }}>
                preview only — exports don't include music yet
              </Typography>
            </Box>
          </Box>

          {/* ── RIGHT: the cut player (docked; pops out to float anywhere) ── */}
          <Box sx={{ flex: { md: "0 0 300px" }, minWidth: 0, display: "flex", flexDirection: "column", gap: "6px" }}>
            <CutPlayer
              picks={picks}
              videoUrl={videoUrl}
              seekReq={seekReq}
              onProgress={setProg}
              floating={floating}
              onToggleFloat={() => setFloating((f) => !f)}
              music={{ url: musicUrl, vol: musicVol }}
            />
            {floating && (
              <Box sx={{ border: "1px dashed rgba(255,255,255,0.18)", borderRadius: "10px", p: "14px", textAlign: "center" }}>
                <Typography sx={{ fontFamily: font, fontSize: "11px", color: "#8d94a8" }}>
                  Player popped out — drag it anywhere, or dock it back here.
                </Typography>
              </Box>
            )}
            {!videoUrl && (
              <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: "#8d94a8", textAlign: "center" }}>
                Preview player unavailable for this demo source.
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

/* ══════════════════════════ ET + TIMELINE, FUSED ══════════════════════════ */
export const FlagshipEtTimeline: React.FC = () => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [shareSrc, setShareSrc] = useState<string>(getDemoShareSrc());
  const [zoom, setZoom] = useState(0.7); // legible clips/text by default (visitors can −/+); the Black Cherry panel still fits thanks to the taller window below
  const [fs, setFs] = useState(false);
  const [picks, setPicks] = useState<Pick[]>([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [chatThread, setChatThread] = useState<ChatMsg[]>([]);
  const [chatBusy, setChatBusy] = useState(false);
  const chatTimerRef = useRef<number | null>(null);
  const [rowIds, setRowIds] = useState<ClipId[]>([]); // all clip ids the ET announced (for auto-seed)
  const picksRef = useRef<Pick[]>([]);
  const seededRef = useRef(false);

  useEffect(() => {
    const el = hostRef.current;
    if (!el || inView) return;
    const io = new IntersectionObserver(
      (es) => { if (es.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); } },
      { rootMargin: "500px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  // demo-video picker announces a new /s/ src
  useEffect(() => {
    const onSrc = (e: Event) => {
      const next = (e as CustomEvent).detail;
      if (typeof next === "string" && next) { setShareSrc(next); setLoaded(false); setPicks([]); setVideoUrl(""); setChatThread([]); setChatBusy(false); setRowIds([]); seededRef.current = false; }
    };
    window.addEventListener(DEMO_SRC_EVENT, onSrc);
    return () => window.removeEventListener(DEMO_SRC_EVENT, onSrc);
  }, []);

  // ── the pick bridge: the embedded ET posts its ordered pick list on every change ──
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (!e.data) return;
      // only trust our own iframe (works same-origin AND for the prod-hosted samples)
      if (frameRef.current && e.source !== frameRef.current.contentWindow) return;
      if (e.data.type === "cc-et-picks" && Array.isArray(e.data.picks)) {
        setPicks(e.data.picks as Pick[]);
        if (typeof e.data.video === "string" && e.data.video) setVideoUrl(e.data.video);
      } else if (e.data.type === "cc-et-ai-reply" && typeof e.data.text === "string") {
        if (chatTimerRef.current) { window.clearTimeout(chatTimerRef.current); chatTimerRef.current = null; }
        setChatBusy(false);
        setChatThread((t) => [...t, { role: "assistant", text: e.data.text, applied: Number(e.data.applied) || 0 }]);
      } else if (e.data.type === "cc-et-rows" && Array.isArray(e.data.rows)) {
        // the ET announces its full row list on load — capture ids so we can auto-seed a pick or two
        setRowIds((e.data.rows as Array<{ id: ClipId }>).map((r) => r.id).filter((id) => id != null));
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const sendChat = (text: string) => {
    setChatThread((t) => [...t, { role: "user", text }]);
    setChatBusy(true);
    sendCmd({ cmd: "ai-chat", text });
    if (chatTimerRef.current) window.clearTimeout(chatTimerRef.current);
    chatTimerRef.current = window.setTimeout(() => {
      setChatBusy(false);
      setChatThread((t) => [...t, { role: "assistant", text: "⚠️ No reply from the editor. If AI isn't set up for this demo, open the editor's Black Cherry button to add a key — or the share link may have used up its guest AI." }]);
    }, 90000);
  };

  const src = inView ? shareSrc : "";

  // commands back INTO the embedded ET (trim / reorder / deselect) — explicit target origin
  const sendCmd: SendCmd = (c) => {
    const w = frameRef.current?.contentWindow;
    if (!w) return;
    let target = window.location.origin;
    try { if (/^https?:\/\//.test(shareSrc)) target = new URL(shareSrc).origin; } catch (e) { /* ignore */ }
    w.postMessage({ type: "cc-et-cmd", ...c }, target);
  };

  // keep a live mirror of picks for the seeder's timeout closure
  useEffect(() => { picksRef.current = picks; }, [picks]);

  // always-fresh mirrors so the [] window-event listener never reads a stale closure
  const sendCmdRef = useRef<SendCmd>(sendCmd);
  sendCmdRef.current = sendCmd;
  const rowIdsRef = useRef<ClipId[]>([]);
  rowIdsRef.current = rowIds;

  // The marketing "Two ways to cut" cards drive THIS live editor from afar:
  //   • CherryPick  → tick a few clips, so the visitor watches a selection happen.
  //   • Black Cherry → open the Black Cherry panel in the editor.
  // Each is fired a few times (idempotent) so it still lands if the ET is mid-load.
  useEffect(() => {
    const burst = (fn: () => void) => [250, 800, 1600].forEach((ms) => window.setTimeout(fn, ms));
    const onOpenBC = () => burst(() => sendCmdRef.current({ cmd: "blackcherry" }));
    const onDemoSelect = () => burst(() => { const ids = rowIdsRef.current.slice(0, 3); if (ids.length) sendCmdRef.current({ cmd: "select", ids }); });
    window.addEventListener("cc-open-blackcherry", onOpenBC);
    window.addEventListener("cc-demo-select", onDemoSelect);
    return () => { window.removeEventListener("cc-open-blackcherry", onOpenBC); window.removeEventListener("cc-demo-select", onDemoSelect); };
  }, []);

  // Auto-seed: once the ET has announced its rows, tick a couple of clips so the timeline
  // demonstrates itself (an empty timeline reads as "broken"). One-shot per source, and it
  // BAILS if the visitor already ticked something — it never fights a real selection. Uses the
  // ET's existing "select" bridge command (same one the Premiere panel uses), so nothing in the
  // live editor changes.
  useEffect(() => {
    if (seededRef.current || !rowIds.length) return undefined;
    if (picks.length) { seededRef.current = true; return undefined; }
    const t = window.setTimeout(() => {
      if (!picksRef.current.length && rowIds.length) sendCmd({ cmd: "select", ids: rowIds.slice(0, 2) });
      seededRef.current = true;
    }, 1500);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowIds, picks]);

  return (
    <Box ref={hostRef} sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      {/* one clear line for the ROW + header actions (which live in the rows/headers, not the toolbar) */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: "10px" }}>
        <Box sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 700, color: CHERRY, border: `1px solid ${CHERRY}55`, background: "rgba(230,29,80,0.07)", borderRadius: "999px", px: "14px", py: "7px", textAlign: "center" }}>
          Each row is a clip — tick ☑ to pick · click ▶ to play · trim − / + · click a column header ↕ to sort
        </Box>
      </Box>
      {/* toolbar arrows — single-purpose, aimed DOWN at the real toolbar sections (x% measured against
          the editor at 70% zoom: Filter / Feeling / Speaker on the left, Search near the middle-right).
          Sort lives in the column HEADERS (see the row-actions line) and the AI co-pilot is the Black
          Cherry chat BELOW — so neither is labeled up here. md+ only. */}
      <Box sx={{ position: "relative", height: 50, mb: "2px", display: { xs: "none", md: "block" }, "@keyframes ccArrowBob": { "0%,100%": { transform: "translateY(0)" }, "50%": { transform: "translateY(4px)" } } }}>
        {[
          { x: "14%", t: "🎚 Filter" },
          { x: "25%", t: "😀 Feeling" },
          { x: "38%", t: "🗣 Speaker" },
          { x: "71%", t: "🔎 Search" },
        ].map((a) => (
          <Box key={a.t} sx={{ position: "absolute", left: a.x, bottom: 0, transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box sx={{ fontFamily: font, fontSize: "11.5px", fontWeight: 800, color: "#fff", background: CHERRY, borderRadius: "999px", px: "11px", py: "6px", whiteSpace: "nowrap", boxShadow: "0 8px 20px -8px rgba(0,0,0,0.5)" }}>{a.t}</Box>
            <Box aria-hidden sx={{ fontSize: "16px", lineHeight: 1, color: CHERRY, mt: "1px", animation: "ccArrowBob 1.5s ease-in-out infinite" }}>▼</Box>
          </Box>
        ))}
      </Box>
      {/* xs: a simple legend instead of arrows */}
      <Box sx={{ display: { xs: "flex", md: "none" }, flexWrap: "wrap", justifyContent: "center", gap: "6px", mb: "10px" }}>
        {["🎚 Filter · sort", "🔎 Search", "🤖 AI", "☑ Pick · ▶ Play"].map((t) => (
          <Box key={t} sx={{ fontFamily: font, fontSize: "11px", fontWeight: 700, color: CHERRY, border: `1px solid ${CHERRY}55`, background: "rgba(230,29,80,0.07)", borderRadius: "999px", px: "10px", py: "5px", whiteSpace: "nowrap" }}>
            {t}
          </Box>
        ))}
      </Box>
      {/* editor window */}
      <Box sx={{ borderRadius: "14px 14px 0 0", overflow: "hidden", border: "1px solid rgba(255,255,255,0.12)", borderBottom: "none", boxShadow: "0 40px 90px -55px rgba(0,0,0,0.9), 0 0 0 1px rgba(230,29,80,0.10)", background: "#0b0a0f" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", px: "13px", py: "8px", borderBottom: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
          <Box sx={{ display: "flex", gap: "6px" }}>
            {["#ff5f57", "#febc2e", "#28c840"].map((c) => (
              <Box key={c} sx={{ width: 11, height: 11, borderRadius: "50%", background: c, opacity: 0.55 }} />
            ))}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <Box aria-hidden sx={{ width: 7, height: 7, borderRadius: "50%", background: CHERRY, boxShadow: "0 0 0 3px rgba(230,29,80,0.18)" }} />
            <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "12.5px", color: "#f3f4f9", display: { xs: "none", sm: "block" } }}>
              The spreadsheet — live
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: "2px", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "7px", overflow: "hidden" }}>
            <Box component="button" onClick={() => setZoom((z) => clamp(z - 0.1, 0.35, 1.1))} title="Zoom out" sx={zoomBtn}>−</Box>
            <Typography sx={{ fontFamily: font, fontSize: "11px", color: "#c2c7d6", px: "5px", minWidth: 34, textAlign: "center" }}>{Math.round(zoom * 100)}%</Typography>
            <Box component="button" onClick={() => setZoom((z) => clamp(z + 0.1, 0.35, 1.1))} title="Zoom in" sx={zoomBtn}>+</Box>
          </Box>
          <Box component="button" onClick={() => setFs(true)} title="Open full screen" sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px", background: "transparent", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "7px", color: "#c2c7d6", fontFamily: font, fontSize: "11px", px: "8px", py: "4px", "&:hover": { color: "#fff", borderColor: "rgba(255,255,255,0.3)" } }}>
            <OpenInFullIcon sx={{ fontSize: 13 }} /> Full screen
          </Box>
        </Box>

        <Box sx={{ position: "relative", height: { xs: "520px", md: "680px" } }}>
          {src ? (
            <iframe
              ref={frameRef}
              title="CherryClipz — live spreadsheet"
              src={src}
              allow="fullscreen; clipboard-write"
              onLoad={() => setLoaded(true)}
              style={iframeStyle(zoom)}
            />
          ) : null}
          {!loaded && (
            <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", background: "#0b0a0f" }}>
              <img src={logoSite} alt="" width={36} style={{ opacity: 0.85 }} />
              <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#9aa0b4" }}>Loading the live editor…</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* the seam — where spreadsheet meets timeline. Two ways in, side by side:
          tick clips yourself up top, or hand the same cut to Black Cherry right here. */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "7px", px: "13px", py: "12px", border: "1px solid rgba(139,92,246,0.32)", borderLeft: "1px solid rgba(255,255,255,0.12)", borderRight: "1px solid rgba(255,255,255,0.12)", background: "rgba(139,92,246,0.05)" }}>
        <ChatDock thread={chatThread} busy={chatBusy} onSend={sendChat} />
      </Box>

      <EditTimeline picks={picks} videoUrl={videoUrl} sendCmd={sendCmd} />

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px", mt: "10px" }}>
        <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#8d94a8" }}>
          <Box component="span" sx={{ color: CHERRY }}>↑</Box> Real editor, real timeline — not a screenshot. Tick a checkbox and watch.
        </Typography>
        <DemoVideoPicker />
      </Box>

      {fs && (
        <Box onClick={() => setFs(false)} sx={{ position: "fixed", inset: 0, zIndex: 13000, background: "rgba(4,3,7,0.72)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", p: { xs: "8px", md: "18px" } }}>
          <Box onClick={(e: React.MouseEvent) => e.stopPropagation()} sx={{ width: "100%", maxWidth: "1500px", height: "92vh", display: "flex", flexDirection: "column", background: "#0b0a0f", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "14px", overflow: "hidden" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", px: "12px", py: "8px", borderBottom: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 }}>
              <Box component="button" onClick={() => setFs(false)} title="Close" sx={{ cursor: "pointer", background: "transparent", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "7px", color: "#c2c7d6", fontFamily: font, fontSize: "12px", px: "10px", py: "5px", "&:hover": { color: "#fff", borderColor: "rgba(255,255,255,0.3)" } }}>Close ×</Box>
            </Box>
            <Box sx={{ position: "relative", flex: 1, overflow: "hidden" }}>
              {src ? <iframe title="CherryClipz — full editor" src={src} allow="fullscreen; clipboard-write" style={iframeStyle(0.82)} /> : null}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
